/**
 * Tab *Theo người dùng* — FR-070…074, TC-24, TC-25.
 * Bám nguyên `permissions.html` → `[data-panel="by-user"]`:
 *
 *   .card-body > .split
 *     ├─ .card (card-head · .pick-search · .card-body > .picker)
 *     └─ .card (card-head · .table-wrap > table.tbl · .empty · .card-foot)
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useT } from '../../lib/i18n';
import { avatarTone, useFormat } from '../../lib/format';
import { Icon } from '../../components/Icon';
import { AsyncSection, EmptyState, SkeletonCard } from '../../components/States';
import { useToast } from '../../components/Toast';
import { AddDomainToUserDialog } from './AddDomainToUserDialog';
import { GrantLevelSelect, RelationChip } from './GrantLevelSelect';
import { permApi, permKeys, type PermissionUserView, type UserDomainAccessView } from './api';

export function ByUserTab() {
  const { formatDateTime } = useFormat();
  const t = useT();
  const { toast, toastError } = useToast();
  const { can } = useAuth();
  // Chặn ở FE chỉ là trải nghiệm — backend kiểm cả hai tầng (docs/05 §5.1).
  const canShare = can('share.grant');
  const canRevoke = can('share.revoke');
  const queryClient = useQueryClient();

  const [filter, setFilter] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  /** Tên miền đang chờ chuyển quyền sở hữu sang người đang chọn. */

  const usersQuery = useQuery({
    queryKey: permKeys.users(''),
    queryFn: () => permApi.users(''),
  });
  const rolesQuery = useQuery({
    queryKey: permKeys.domainRoles,
    queryFn: permApi.domainRoles,
    staleTime: 10 * 60 * 1000,
  });

  const users = usersQuery.data ?? [];
  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return users;
    return users.filter((user) => `${user.fullName} ${user.email}`.toLowerCase().includes(q));
  }, [users, filter]);

  const selected: PermissionUserView | null =
    users.find((user) => user.id === selectedId) ?? filtered[0] ?? users[0] ?? null;

  const accessQuery = useQuery({
    queryKey: permKeys.userDomains(selected?.id ?? ''),
    queryFn: () => permApi.userDomains(selected!.id),
    enabled: Boolean(selected),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: permKeys.all });
  const onError = (err: unknown) =>
    toastError(err instanceof ApiError ? err.message : t('state.error.title'));

  const revoke = useMutation({
    mutationFn: (row: UserDomainAccessView) => permApi.revokeGrant(row.domain.id, row.grantId!),
    onSuccess: async (_data, row) => {
      await invalidate();
      toast(
        t('perm.revoked', {
          name: selected?.fullName ?? '',
          domain: row.domain.name,
        }),
      );
    },
    onError,
  });

  return (
    <div className="card-body">
      <div className="split">
        {/* ── Cột trái: người dùng (FR-070) ─────────────────── */}
        <div className="card">
          <div className="card-head" style={{ padding: '10px 12px' }}>
            <span className="eyebrow grow">{t('perm.users')}</span>
            <span className="small muted">
              {filtered.length}/{users.length}
            </span>
          </div>
          <div className="pick-search">
            <input
              type="text"
              placeholder={t('perm.filterUsers')}
              aria-label={t('perm.filterUsers')}
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
            />
          </div>
          <div className="card-body" style={{ padding: 8 }}>
            <div className="picker scroll" data-od-id="perm-user-picker">
              {usersQuery.isLoading ? (
                <SkeletonCard rows={4} />
              ) : filtered.length === 0 ? (
                <div className="small muted" style={{ padding: 12 }}>
                  {users.length === 0 ? t('perm.emptyNoUsers') : t('perm.noUserMatch')}
                </div>
              ) : (
                filtered.map((user) => (
                  <button
                    key={user.id}
                    type="button"
                    aria-pressed={user.id === selected?.id}
                    onClick={() => setSelectedId(user.id)}
                  >
                    <span className={`avatar sm ${avatarTone(user.email)}`}>{user.initials}</span>
                    <span className="who">
                      <b>{user.fullName}</b>
                      <span>{user.role.name}</span>
                    </span>
                    <span className="n">{user.domainCount}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        {/* ── Cột phải: tên miền người đó thấy (FR-071/072) ──── */}
        <div className="card" data-od-id="perm-user-detail">
          {selected ? (
            <>
              <div className="card-head">
                <span className={`avatar ${avatarTone(selected.email)}`}>{selected.initials}</span>
                <div className="grow">
                  <h2>{selected.fullName}</h2>
                  <div className="sub">
                    <span>{selected.email}</span> · <span>{selected.role.name}</span>
                  </div>
                </div>
                <span className="chip">
                  <span className="dot" />
                  <b>{accessQuery.data?.length ?? selected.domainCount}</b>&nbsp;
                  {t('perm.domainsSeen')}
                </span>
                <button
                  className="btn btn-sm btn-primary"
                  type="button"
                  onClick={() => setShowAdd(true)}
                  data-od-id="perm-add-domain"
                  hidden={!canShare}
                >
                  <Icon name="plus" size={15} />
                  {t('perm.addDomain')}
                </button>
              </div>

              <AsyncSection
                loading={accessQuery.isLoading || rolesQuery.isLoading}
                error={accessQuery.error ?? rolesQuery.error}
                data={accessQuery.data}
                onRetry={() => void accessQuery.refetch()}
                skeleton={<SkeletonCard rows={4} />}
                empty={
                  <EmptyState
                    icon="domains"
                    title={t('perm.emptyUserTitle')}
                    body={t('perm.emptyUserBody')}
                    action={
                      <button className="btn" type="button" onClick={() => setShowAdd(true)}>
                        {t('perm.emptyUserAction')}
                      </button>
                    }
                  />
                }
              >
                {(rows) => (
                  <div className="table-wrap">
                    <table className="tbl" data-od-id="perm-user-table">
                      <thead>
                        <tr>
                          <th scope="col">{t('perm.col.domain')}</th>
                          <th scope="col">{t('perm.col.relation')}</th>
                          <th scope="col">{t('perm.col.level')}</th>
                          <th scope="col">{t('perm.col.grantedAt')}</th>
                          <th scope="col" className="right">
                            {t('perm.col.actions')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row) => (
                          <tr key={row.domain.id}>
                            <td>
                              <b className="mono">{row.domain.name}</b>
                              <div className="sub">
                                {t('perm.recordsCount', { count: row.domain.recordCount })}
                              </div>
                            </td>
                            <td>
                              <RelationChip relation={row.relation} />
                            </td>
                            <td>
                              {row.revocable && row.domainRole && canShare ? (
                                <GrantLevelSelect
                                  value={row.domainRole.id}
                                  roles={rolesQuery.data ?? []}
                                  ariaLabel={`${t('perm.col.level')} ${row.domain.name}`}
                                  onError={onError}
                                  onChange={async (domainRoleId) => {
                                    const updated = await permApi.updateGrant(
                                      row.domain.id,
                                      row.grantId!,
                                      domainRoleId,
                                    );
                                    await invalidate();
                                    toast(
                                      t('perm.levelChanged', {
                                        name: selected.fullName,
                                        domain: row.domain.name,
                                        level: updated.domainRole?.name ?? '',
                                      }),
                                    );
                                  }}
                                />
                              ) : (
                                <span className="small muted">
                                  {row.isOwner ? t('perm.ownerLevel') : t('perm.relation.viewall')}
                                </span>
                              )}
                            </td>
                            <td className="small muted">
                              {row.createdAt
                                ? formatDateTime(row.createdAt)
                                : t('perm.grantedWhenAdded')}
                              {row.grantedBy ? (
                                <div className="sub">
                                  {t('perm.grantedBy', { name: row.grantedBy.fullName })}
                                </div>
                              ) : null}
                            </td>
                            <td className="right">
                              {row.revocable && canRevoke ? (
                                <button
                                  className="btn btn-sm btn-danger"
                                  type="button"
                                  disabled={revoke.isPending}
                                  onClick={() => {
                                    if (
                                      window.confirm(
                                        t('perm.revoked', {
                                          name: selected.fullName,
                                          domain: row.domain.name,
                                        }),
                                      )
                                    ) {
                                      revoke.mutate(row);
                                    }
                                  }}
                                >
                                  {t('perm.revoke')}
                                </button>
                              ) : (
                                <span className="small muted">{t('perm.notRevocable')}</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </AsyncSection>

              <div className="card-foot">
                <span className="small muted">{t('perm.userFootnote')}</span>
              </div>
            </>
          ) : (
            <div className="card-body">
              <EmptyState title={t('perm.emptyNoUsers')} />
            </div>
          )}
        </div>
      </div>

      {selected && showAdd ? (
        <AddDomainToUserDialog open user={selected} onClose={() => setShowAdd(false)} />
      ) : null}
    </div>
  );
}
